import { postJSON } from 'common/services/api'
import dayjs from 'dayjs'
import { useMutation, useQueryClient } from 'react-query'
import {
  MAR_CHART_QUERY_KEY,
  MEDICATION_SCHEDULE_PATTERN_DISCONTINUE_URL,
  MEDICATION_SCHEDULE_PATTERN_QUERY_KEY,
  MEDICATION_SCHEDULE_PATTERNS_QUERY_KEY,
  STOCK_LOGS_QUERY_KEY
} from './queryConstants'

type UseMedicationSchedulePatternDiscontinueReturn = {
  discontinueMedicationSchedulePattern: (reasonDeleted: string) => Promise<void>
  isDiscontinuingMedicationSchedulePattern: boolean
}

export type UseMedicationSchedulePatternDiscontinueProps = {
  medicationId: string
  onSuccess?: () => void
  onError?: () => void
}

const useMedicationSchedulePatternDiscontinue = ({
  medicationId,
  onSuccess,
  onError
}: UseMedicationSchedulePatternDiscontinueProps): UseMedicationSchedulePatternDiscontinueReturn => {
  const queryClient = useQueryClient()

  const discontinueMedicationSchedulePatternFunction = async (
    reason: string
  ): Promise<unknown> => {
    return postJSON({
      url: MEDICATION_SCHEDULE_PATTERN_DISCONTINUE_URL.replace(
        '{id}',
        medicationId
      ),
      method: 'PUT',
      json: {
        reason,
        endDate: dayjs().toISOString()
      }
    })
  }

  const {
    mutateAsync: discontinueMedicationSchedulePatternMutation,
    isLoading: isDiscontinuingMedicationSchedulePattern
  } = useMutation<unknown, unknown, string, unknown>(
    [MEDICATION_SCHEDULE_PATTERN_QUERY_KEY],
    discontinueMedicationSchedulePatternFunction,
    {
      onSuccess: () => {
        queryClient.invalidateQueries([MEDICATION_SCHEDULE_PATTERNS_QUERY_KEY])
        queryClient.invalidateQueries([MEDICATION_SCHEDULE_PATTERN_QUERY_KEY])
        queryClient.invalidateQueries([MAR_CHART_QUERY_KEY])
        queryClient.invalidateQueries([STOCK_LOGS_QUERY_KEY])
        onSuccess?.()
      },
      onError: () => {
        onError?.()
      }
    }
  )

  const discontinueMedicationSchedulePattern = async (
    reason: string
  ): Promise<void> => {
    await discontinueMedicationSchedulePatternMutation(reason)
  }

  return {
    discontinueMedicationSchedulePattern,
    isDiscontinuingMedicationSchedulePattern
  }
}

export default useMedicationSchedulePatternDiscontinue
